import { isEmpty, orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useHistory, useLocation } from 'react-router'

import { getCurrentSpaceId } from '@/components/App/selectors'
import Page from '@/components/TailwindUI/Page'
import RoundSearchBar from '@/components/TailwindUI/RoundSearchBar'
import Skeleton from '@/components/TailwindUI/Skeleton'
import { useNotification } from '@/hooks/useNotification'
import DataSourceWizard from '@/pages/SpaceAppBuilder/components/DataSourceWizard/DataSourceWizard'
import { InfiniteScrollList } from '@/pages/SpaceAppBuilder/LandingPage/components/InfiniteScrollList'
import {
  AppInstanceDataSource,
  DatasourceWizardMode,
  WorkgridCatalogDatasource,
  WorkgridDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { dataSourceV3CatalogQueryOptions, dataSourceV3QueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppSelector } from '@/store/hooks'

import DataSourceRow from './components/DataSourceRow'

const PAGE_SIZE = 20
type DataSourceModal = {
  isOpen: boolean
  mode: DatasourceWizardMode
  datasourceKey: string
  datasourceId: string
  dataSource?: AppInstanceDataSource
}

export default function DataSourcesPage() {
  const [search, setSearch] = useState('')
  const history = useHistory()
  const location = useLocation<{
    isFromDelete: boolean
    name: string
    vendorName: string
  }>()
  const [dataSourceModal, setDataSourceModal] = useState<DataSourceModal>({
    isOpen: false,
    mode: DatasourceWizardMode.CREATE,
    datasourceKey: '',
    datasourceId: ''
  })

  const { notify } = useNotification()

  const [datasourceListSize, setDatasourceListSize] = useState(PAGE_SIZE)

  const currentSpaceId = useAppSelector(getCurrentSpaceId)

  const workgridDatasourcesQueryResult = useQuery<WorkgridDatasource[]>({
    ...dataSourceV3QueryOptions({ spaceId: currentSpaceId })
  })
  const workgridDatasourcesCatalogQueryResult = useQuery<WorkgridCatalogDatasource[]>({
    ...dataSourceV3CatalogQueryOptions({ spaceId: currentSpaceId })
  })

  const workgridDatasourceCatalog = workgridDatasourcesCatalogQueryResult.data ?? []

  function navToDataSource(id: string, datasourceId?: string) {
    if (datasourceId) {
      history.push(`/space/${currentSpaceId}/appbuilder/datasources/${datasourceId}/instances/${id}`)
    }
  }

  const datasources = workgridDatasourcesQueryResult.data as WorkgridDatasource[]
  const filteredDataSources =
    search.length > 0 ? datasources.filter(ds => ds.name?.toLowerCase()?.includes(search?.toLowerCase())) : datasources

  const workgridDatasources = orderBy(filteredDataSources, ['modifiedDate'], ['desc'])

  const isEmpty = !workgridDatasourcesQueryResult?.data?.length
  const isLoading = workgridDatasourcesQueryResult.isLoading

  function openCreateModal(key: string) {
    setDataSourceModal({ ...dataSourceModal, datasourceKey: key, isOpen: true })
  }

  function openBrowseModal() {
    setDataSourceModal({ ...dataSourceModal, datasourceKey: '', isOpen: true })
  }

  useEffect(() => {
    if (location.state?.isFromDelete) {
      notify({
        variant: 'success',
        title: 'Data Source Deleted',
        body: (
          <div className="text-sm text-zinc-500">
            <span className="text-md font-semibold">{location.state?.name}</span>{' '}
            <span className="text-sm">has been successfully deleted.</span>
          </div>
        )
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.isFromDelete])

  return (
    <>
      <DataSourceWizard
        isOpen={dataSourceModal.isOpen}
        mode={dataSourceModal.mode}
        datasourceKey={dataSourceModal.datasourceKey}
        showInstanceSelection={false}
        onClose={() => {
          if (dataSourceModal.mode !== DatasourceWizardMode.PREVIEW) {
            workgridDatasourcesQueryResult.refetch()
          }
          setDataSourceModal({
            ...dataSourceModal,
            isOpen: false,
            ...(dataSourceModal.mode === DatasourceWizardMode.CREATE && { datasourceKey: '' })
          })
        }}
      />
      <Page className="mt-8 px-8">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold text-zinc-900">Data sources</h1>
          <button
            className="ml-4 flex items-center rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-blue-600 shadow ring-2 ring-inset ring-blue-600 hover:bg-zinc-50"
            onClick={() => openBrowseModal()}
          >
            Connect
          </button>
        </div>
        {isLoading ? (
          <LoadingState />
        ) : isEmpty ? (
          <EmptyState
            datasourceCatalog={workgridDatasourceCatalog}
            onCreate={key => openCreateModal(key)}
            onBrowse={() => openBrowseModal()}
          />
        ) : (
          <>
            <div className="mt-6 flex w-full items-center transition-all sm:w-[575px]">
              <RoundSearchBar
                placeholder="Search data sources"
                name="search"
                id="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClear={() => setSearch('')}
              />
            </div>
            <InfiniteScrollList
              disabled={isLoading}
              hasNextPage={datasourceListSize < workgridDatasources.length}
              onLoadMore={() => setDatasourceListSize(datasourceListSize + PAGE_SIZE)}
            >
              <ul className="mt-8 divide-y divide-zinc-100">
                {workgridDatasources.slice(0, datasourceListSize).map(ds => {
                  const workgridDatasource = workgridDatasourceCatalog.find(
                    datasource => 'datasourceId' in ds && datasource.id === ds.datasourceId
                  )
                  const datasource = {
                    iconUrl: workgridDatasource?.iconUrl ?? '',
                    isAuthenticated: ds?.status === 'connected',
                    dataSourceName: workgridDatasource?.name ?? ds.name
                  }

                  return (
                    <DataSourceRow
                      key={ds.id}
                      id={ds.id}
                      name={ds.name}
                      datasource={ds}
                      dataSourceName={datasource.dataSourceName}
                      iconUrl={datasource.iconUrl}
                      isAuthenticated={datasource.isAuthenticated}
                      onSelect={navToDataSource}
                      readOnly={false}
                      appCount={ds.applicationIds?.length || 0}
                    />
                  )
                })}
              </ul>
            </InfiniteScrollList>
          </>
        )}
      </Page>
    </>
  )
}

function LoadingState() {
  return (
    <>
      <div className="mt-8 flex w-[575px] shadow-sm">
        <div className="relative flex flex-grow items-stretch focus-within:z-10">
          <Skeleton className="block h-10 rounded-full py-2 px-10" />
        </div>
      </div>
      <div className="mt-8">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <ul className="min-w-full divide-y divide-zinc-100">
              {[0, 1, 2].map((id: number) => (
                <li key={`loading-${id}`} className="relative flex w-full justify-between gap-x-8 px-6 py-4">
                  <div className="flex w-full items-center transition-all lg:w-2/3">
                    <div className="flex items-center justify-between pr-12">
                      <div className="flex w-96 items-center gap-3">
                        <div className="rounded-lg border bg-white p-2">
                          <Skeleton className="h-10 w-10 rounded-md" />
                        </div>
                        <div>
                          <Skeleton className="h-4 w-48" />
                          <Skeleton className="mt-0.5 h-4 w-64" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full items-center justify-between gap-x-8 transition-all">
                    <div className="flex items-center">
                      <div>
                        <Skeleton className="h-5 w-24" />
                        <Skeleton className="mt-0.5 h-5 w-4" />
                      </div>
                    </div>
                    <div className="fade-in hidden min-w-[100px] items-center lg:flex">
                      <div>
                        <Skeleton className="h-5 w-24" />
                        <Skeleton className="mt-0.5 h-5 w-4" />
                      </div>
                    </div>
                    <div className="flex w-1/2 justify-between gap-x-2" />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}

function EmptyState({
  datasourceCatalog,
  onBrowse,
  onCreate
}: {
  datasourceCatalog: Array<WorkgridCatalogDatasource>
  onBrowse: () => void
  onCreate: (key: string) => void
}) {
  const featuredDataSources = [
    {
      description: 'Streamline expense management and financial workflows',
      key: 'chromeriver-workshop'
    },
    {
      description: 'Manage your incidents, priorities, statuses and more',
      key: 'jira'
    },
    {
      description: 'Manage calendars and sync to a variety of Microsoft endpoints',
      key: 'microsoftgraph-workshop'
    },
    {
      description: 'Manage contacts, leads, accounts, opportunities and more',
      key: 'sfdc'
    },
    {
      description: 'Gain access to pre-trained generative models',
      key: 'azureopenai'
    },
    {
      description: 'Handle service management for every department in the enterprise',
      key: 'servicenow-workshop'
    }
  ]
  return (
    <div className="mt-8 mb-2 flex">
      <div className="w-full">
        <h2 className="text-lg font-semibold leading-8 text-zinc-900">You have no saved data source instances</h2>
        <p className="mt-1 text-sm text-zinc-500">
          Quickly get started with one of the data sources below, or "Connect" above to find one that suits your needs.
        </p>
        <div className="mt-5">
          <button onClick={() => onBrowse()} className="text-sm font-semibold text-blue-600 hover:text-blue-500">
            Browse all data sources <span aria-hidden="true"> &rarr;</span>
          </button>
        </div>
        {!isEmpty(datasourceCatalog) && (
          <>
            <h3 className="zinc-400 mt-8 text-sm">Example data sources</h3>
            <ul className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
              {featuredDataSources.map((item, itemIdx) => (
                <li key={itemIdx} className="flow-root">
                  <div className="relative -my-1 flex items-center space-x-4 rounded-xl border border-zinc-100 p-3 shadow focus-within:ring-2 focus-within:ring-blue-500 hover:bg-zinc-50">
                    <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg">
                      <img
                        className="rounded-md"
                        src={datasourceCatalog.find(datasource => datasource.id === item.key)?.iconUrl}
                        alt=""
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-900">
                        <button onClick={() => onCreate(item.key)} className="focus:outline-none">
                          <span className="absolute inset-0" aria-hidden="true" />
                          <span>{datasourceCatalog.find(datasource => datasource.id === item.key)?.name}</span>
                        </button>
                      </h3>
                      <p className="mt-1 text-sm text-zinc-500">{item.description}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
